<script setup lang="ts">
import { mdiMagnify, mdiCloseCircle } from '@mdi/js'
import { vOnClickOutside } from '@vueuse/components'
import SvgIcon from '@/shared/components/icon/SvgIcon.vue'
import BaseButton from '@/shared/components/button/BaseButton.vue'
import { toKebabCase } from '@/shared/utils/format'

interface Props {
  title: string
}

interface Emits {
  (e: 'change'): void
  (e: 'clear'): void
  (e: 'open'): void
}

defineOptions({ inheritAttrs: false })
const props = defineProps<Props>()
const emits = defineEmits<Emits>()
const model = defineModel<string>({ required: true })
const active = defineModel<boolean>('active', { required: true })
const focus = defineModel<boolean>('focus', { required: true })
const name = toKebabCase(props.title)

const setFocus = (state: boolean) => {
  focus.value = state
}

const open = () => {
  setFocus(true)
  emits('open')
}

const change = () => {
  setFocus(true)
  emits('change')
}

const close = () => {
  if (model.value.length === 0) {
    setFocus(false)
  }
}

const clear = () => {
  model.value = ''
  emits('clear')
}
</script>

<template>
  <div class="relative" :class="{ 'shadow-md': active }" v-on-click-outside="() => close()">
    <div class="absolute inset-y-0 flex items-center ms-2">
      <SvgIcon
        :path="mdiMagnify"
        class="h-5 w-5"
        :class="active ? 'fill-gray-700' : 'fill-white'"
      />
    </div>
    <input
      :id="name"
      :name="name"
      type="text"
      autocomplete="off"
      spellcheck="false"
      v-model="model"
      class="block w-full placeholder:text-white border text-sm outline-none transition-all duration-200 ease-in-out pr-20 ps-8 px-2 py-1"
      :class="[
        { 'mr-64': focus },
        active ? 'mr-64 bg-white border-gray-300' : 'border-white text-white',
      ]"
      @keydown.enter="change()"
      @click="open()"
      v-bind="$attrs"
    />
    <label
      :for="name"
      v-if="!focus && model.length === 0"
      class="pointer-events-none text-sm absolute inset-y-0 start-8 flex items-center"
    >
      <div class="text-white px-1">
        {{ $t('placeholder.search') }}
      </div>
    </label>
    <div class="absolute inset-y-0 end-2 flex items-center ms-4"></div>
    <div v-if="model && model.length > 0" class="absolute inset-y-0 end-2 flex items-center ms-4">
      <BaseButton :title="$t('common.clear')" @click="clear()">
        <SvgIcon
          :path="mdiCloseCircle"
          class="h-5 w-5"
          :class="active ? 'fill-gray-700' : 'fill-white'"
        />
      </BaseButton>
    </div>
  </div>
</template>
